/**
 * usePebbles hook
 * React Query hook for fetching user profile with Pebbles count
 * Provides totalPebbles with graceful error fallback to 0
 */

import { useQuery } from '@tanstack/react-query';
import { getUserProfile } from '@/services/pebbles';
import type { UserProfile } from '@/types/pebbles';

export interface UsePebblesReturn {
  /** Total pebbles count (defaults to 0) */
  totalPebbles: number;
  /** Full user profile data */
  profile: UserProfile | undefined;
  /** Loading state - true during initial fetch */
  isLoading: boolean;
  /** Error object if fetch failed */
  error: Error | null;
  /** Function to manually refetch */
  refetch: () => void;
}

export function usePebbles(): UsePebblesReturn {
  const {
    data: profile,
    isLoading,
    error,
    refetch,
  } = useQuery<UserProfile, Error>({
    queryKey: ['user', 'profile'],
    queryFn: () => getUserProfile(),
    staleTime: 30000, // 30 seconds (same as useSessions)
  });

  return {
    totalPebbles: profile?.totalPebbles ?? 0,
    profile,
    isLoading,
    error,
    refetch: () => {
      refetch();
    },
  };
}
